import { FC, useEffect, useRef, useState } from 'react';
import { Form } from 'antd';
import { useMethods } from '@lhb/hook';
import FormSetName from '@/common/components/Form/FormSetName/FormSetName';
import { postChancePointCollisionCheck } from '@/common/api/expandStore/chancepoint';
import { formDataComsToTiled, hasAreaProtectionAndAddress, parseValueCatch } from '../../config';
import { addressIdentification } from '../../customize';
import { adaptCollisionCheckResult, AreaProtectionResult, CollisionCheckGroup, getCollisionCoordinates, hasCollisionWithin200m, hasInitiatedSpecialApplication, shouldPersistSpecialApplication, trimCollisionGroups } from './collision';
import AreaProtectionPanel from './AreaProtectionPanel';

export type ApprovalStatus = 'pending' | 'approving' | 'approved' | 'rejected';

const AreaProtection: FC<{
  form: any;
  formItemData: any;
  wholeFormData: any;
  /** 地址组件本次变更的即时坐标，避免等待异步地址详情回写。 */
  addressVal?: any;
  valueChange?: any;
  onCollisionChange?: (hasCollisionWithin200m: boolean | undefined) => void;
}> = ({ form, formItemData, wholeFormData, addressVal, valueChange, onCollisionChange }) => {
  const identification = formItemData.identification;
  Form.useWatch(addressIdentification, form);
  const persistedValue = parseValueCatch(formItemData) || {};
  const persistedGroups: CollisionCheckGroup[] = Array.isArray(persistedValue?.groups) ? persistedValue.groups : [];
  const persistedStatus: ApprovalStatus = hasInitiatedSpecialApplication(persistedValue?.approvalStatus) ? persistedValue.approvalStatus : 'pending';
  const [status, setStatus] = useState<ApprovalStatus>(persistedStatus);
  const [approvalOpinion, setApprovalOpinion] = useState(persistedValue?.approvalOpinion || '');
  const [collisionGroups, setCollisionGroups] = useState<CollisionCheckGroup[]>(persistedGroups);
  const [result, setResult] = useState<AreaProtectionResult | undefined>(hasInitiatedSpecialApplication(persistedStatus) ? adaptCollisionCheckResult(persistedGroups) : undefined);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);
  const allComponents: any[] = [];
  const hasAreaProtection = hasAreaProtectionAndAddress(wholeFormData?.propertyGroupVOList);
  hasAreaProtection && formDataComsToTiled(wholeFormData?.propertyGroupVOList, allComponents);
  const addressComponent = allComponents.find((item: any) => item.identification === addressIdentification);
  let addressValue: any = addressComponent?.textValue;
  try {
    addressValue = typeof addressValue === 'string' ? JSON.parse(addressValue) : addressValue;
  } catch (error) {
    console.log('区域保护解析详细地址坐标失败', error);
    addressValue = null;
  }
  const coordinates = getCollisionCoordinates(addressValue, addressVal);
  const [longitude, latitude] = coordinates || [NaN, NaN];
  const hasValidCoordinates = Number.isFinite(longitude) && Number.isFinite(latitude);
  const hasInitiatedApplication = shouldPersistSpecialApplication(status, collisionGroups);
  const methods = useMethods({
    syncValue() {
      if (!hasAreaProtection) return;
      // 保存时仅保留 200m 内撞店分组；未撞店时必须将 textValue 置空。
      const value = hasCollisionWithin200m(collisionGroups) ? {
        groups: trimCollisionGroups(collisionGroups),
      } : null;
      form.setFieldValue(identification, value);
      valueChange?.(identification, value);
    },
    resetStatus() {
      setStatus('pending');
      setApprovalOpinion('');
    },
    queryCollision() {
      if (!hasAreaProtection) return;
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      if (!hasValidCoordinates) {
        setCollisionGroups([]);
        setResult(undefined);
        onCollisionChange?.(undefined);
        setIsLoading(false);
        return;
      }
      // 地址已变更时先清空上一地址的检测结果，避免保存时沿用旧坐标的撞店状态。
      setCollisionGroups([]);
      setResult(undefined);
      setIsLoading(true);
      onCollisionChange?.(undefined);
      postChancePointCollisionCheck({ lng: longitude, lat: latitude, chancePointId: wholeFormData?.chanceId ?? wholeFormData?.id ?? null }).then((data: any) => {
        if (requestId === requestIdRef.current) {
          const groups = Array.isArray(data?.groups) ? data.groups : [];
          setCollisionGroups(groups);
          const adaptedResult = adaptCollisionCheckResult(groups);
          setResult(adaptedResult);
          onCollisionChange?.(adaptedResult.allowApply);
        }
      }, (error) => {
        console.log('查询区域保护防撞店失败', error);
        if (requestId === requestIdRef.current) {
          setResult(undefined);
          onCollisionChange?.(undefined);
        }
      }).finally(() => {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
        }
      });
    },
    initializeCollision() {
      if (!hasAreaProtection) return;
      if (hasInitiatedApplication) {
        const adaptedResult = adaptCollisionCheckResult(collisionGroups);
        setResult(adaptedResult);
        onCollisionChange?.(adaptedResult.allowApply);
        return;
      }
      methods.resetStatus();
      methods.queryCollision();
    },
  });

  useEffect(methods.initializeCollision, [longitude, latitude, hasInitiatedApplication, hasAreaProtection]);

  useEffect(methods.syncValue, [collisionGroups, result, status, approvalOpinion, hasAreaProtection]);

  return hasAreaProtection ? <>
    <FormSetName name={identification} initialValue={{}} />
    <AreaProtectionPanel
      result={result}
      isLoading={isLoading}
      approvalStatus={status}
      approvalOpinion={approvalOpinion}
    />
  </> : null;
};

export default AreaProtection;
